import { Injectable } from '@angular/core';
import MixPanel from 'mixpanel-browser';
import { MIXPANEL_DEBUG, MIXPANEL_TOKEN } from '@app/_helpers';
import { ConfigurationService } from '../configuration/configuration.service';
import { MIXPANEL_METHODS } from './mixpanel.events';

type Props = Record<string, any>;

@Injectable({ providedIn: 'root' })
export class MixpanelService {
  private initialized = false;
  private disabled = false;

  private initPromise?: Promise<void>;

  private queue: Array<{ fn: MIXPANEL_METHODS; args: any[] }> = [];
  private readonly MAX_QUEUE = 200;

  constructor(
    private configService: ConfigurationService
  ) {}

  init(): Promise<void> {
    if (this.disabled || this.initialized) return Promise.resolve();
  
    // ✅ If init already started, return the same promise
    if (this.initPromise) return this.initPromise;
  
    this.initPromise = (async () => {
      try {
        this.disabled = !!this.configService.remoteConfigCacheObj.MIXPANEL_CONFIG?.disabled;
        if (this.disabled) return;
  
        const token = (this.configService.remoteConfigCacheObj.MIXPANEL_CONFIG?.token || MIXPANEL_TOKEN || '').trim();
        if (!token) { this.disabled = true; return; }
  
        MixPanel.init(token, {
          debug: !!MIXPANEL_DEBUG,
          autocapture: false,
          record_sessions_percent: 0,
          track_pageview: false,
          persistence: 'localStorage', // Store data in localStorage instead of cookies (cookies are attached to every http request)
          cookie_expiration: 7, // Cookies will expire in 7 days
          cross_subdomain_cookie: false, // Don't share cookies across subdomains
          save_referrer: false,  // disable Mixpanel’s full URL storage
          store_google: false,   // we store UTMs ourselves above
        } as any);

        const attribution = this.getSanitizedAttribution();
        // register_once = first-touch attribution
        MixPanel.register_once(attribution);
  
        this.initialized = true;
        this.flush();
      } catch (error) {
        console.error('Mixpanel initialization failed', error);
        this.disabled = true;
        this.queue = [];
      } finally {
        // allow GC; keep idempotency via `initialized`/`disabled`
        this.initPromise = undefined;
      }
    })();
  
    return this.initPromise;
  }

  private getSanitizedAttribution() {
    // referrer are already provided in built-in mixpanel properties in every event; as such not needed in custom properties.
    const url = new URL(window.location.href);
    // keep only safe UTMs
    const allowUtm = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    const utm: Record<string, string> = {};
    for (const k of allowUtm) {
      const v = url.searchParams.get(k);
      const containsPII = (value: string) =>
        /@|email|phone|name=/i.test(value);

      if (v && !containsPII(v)) {
        utm[k] = v?.slice(0, 80);
      }
    }
    return { ...utm };
  }

  track(event: string, properties?: Props): void {
    this.call(MIXPANEL_METHODS.Track, [event, properties]);
  }

  identify(userId: string): void {
    this.call(MIXPANEL_METHODS.Identify, [userId]);
  }

  setUserProperties(properties: Props): void {
    this.call(MIXPANEL_METHODS.PeopleSet, [properties]);
  }

  reset(): void {
    this.call(MIXPANEL_METHODS.Reset, []);
  }

  private call(fn: MIXPANEL_METHODS, args: any[]): void {
    if (this.disabled) return;

    try {
      if (!this.initialized) {
        this.enqueue(fn, args);
        return;
      }

      switch (fn) {
        case MIXPANEL_METHODS.Track: MixPanel.track(args[0], args[1]); break;
        case MIXPANEL_METHODS.Identify: MixPanel.identify(args[0]); break;
        case MIXPANEL_METHODS.PeopleSet: MixPanel.people.set(args[0]); break;
        case MIXPANEL_METHODS.Reset: MixPanel.reset(); break;
      }
    } catch {
      // Hard-disable after a throw, so it can never keep hurting UX
      this.disabled = true;
      this.queue = [];
    }
  }

  private enqueue(fn: any, args: any[]) {
    if (this.queue.length >= this.MAX_QUEUE) this.queue.shift();
    this.queue.push({ fn, args });
  }

  private flush() {
    const items = this.queue;
    this.queue = [];
    for (const it of items) this.call(it.fn, it.args);
  }
}
